import { z } from "zod";

export {
  showGenreNestedSchema,
  type ShowGenreNestedType,
} from "@/pages/movies/schemas/movie.nested.form";

/**
 * One `PlayCastRequestDto` entry. A member is either *linked* to a Person (has `personId`)
 * or *unknown* (free-text `name`) — never both; `UpdatePlayCommandHandler` fails the whole
 * update otherwise. Under decision D1 most Greek stage credits are the unknown kind, and
 * that is the expected state, not a defect.
 *
 * `role` is what the backend calls the character on a *stored* `PlayCastDbDto`
 * (`services/shared/.../PlayCastDbDto.cs`); the request DTO calls the same thing
 * `characterName`, and `UpdatePlayCommandHandler`/`CreatePlayCommandHandler` map
 * `Role = c.CharacterName`. A plain `z.object` here — not a `z.preprocess`-wrapped one —
 * is deliberate: `@autoform/zod`'s `getFieldConfigInZodStack` only understands a
 * `ZodEffects` produced by `.refine()`/`.superRefine()` (`_def.effect.refinement`); a
 * `z.preprocess()` effect has `_def.effect.transform` instead, so `effect.refinement` is
 * `undefined` and the walker's `in` check throws
 * `Cannot use 'in' operator to search for 'Symbol(GetFieldConfig)' in undefined` — every
 * play with a cast crashed `AutoForm`. The `role` → `characterName` fold now happens once,
 * on the raw record, in `normalizePlayRecord` (`play.edit.form.ts`) before the record ever
 * reaches this schema; the parsed shape and every write only ever carry `characterName` —
 * `role` is accepted as input there, never as output.
 */
export const playCastNestedSchema = z.object({
  personId: z.string().nullish(),
  name: z.string().nullish(),
  /**
   * Name-only credits: the locale `name` is written in (`el` for a Greek bill) and, when `name`
   * is not in Latin script, a Latin-alphabet form of it. Tina fills both from the source page;
   * they must survive a curator save, which rebuilds the whole cast list.
   */
  nameLocale: z.string().nullish(),
  latinName: z.string().nullish(),
  characterName: z.string().nullish(),
  department: z.string().default("Acting"),
  photo: z.object({ url: z.string(), blurhash: z.string() }).nullish(),
});

export type PlayCastNestedType = z.infer<typeof playCastNestedSchema>;
